import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { SeriesAsset } from '../../../queries/ats/titles/types/asset';
import { bySeason, getSeasons } from '../../../queries/ats/titles/utils/array-utils';
import { useDetails } from '../details-context';
import { EpisodeList, EpisodeListSkeleton } from '../details-season/episode-list/episode-list';
import { SeasonSelector, SeasonSelectorSkeleton } from '../details-season/season-selector/season-selector';
import css from './details-series-view.module.scss';
import { useActiveSeason } from './use-active-season';

export interface DetailsSeriesViewProps {
  asset: SeriesAsset;
}

export function DetailsSeriesView({ asset }: DetailsSeriesViewProps) {
  const { selectedContext, focusedAsset } = useDetails('DetailsSeriesView');
  const { t } = useTranslation();

  if (focusedAsset.type !== 'episode') {
    throw new Error('Expected focused asset to be of type episode');
  }

  const episodes = selectedContext.episodes(asset);
  const seasons = getSeasons(episodes);

  const [activeSeason, setActiveSeason] = useActiveSeason(seasons, focusedAsset);
  const episodesInSeason = episodes.filter(bySeason(activeSeason));

  const newsGenres = ['nyheter', 'news', 'nyheder'];
  const isNews = asset.genres.some((genre) => newsGenres.includes(genre.toLowerCase()));

  const nonDateBased = ['sport magazine', 'sport magasin', 'sport magasinet', 'documentary', 'dokumentar'];
  const isSport =
    asset.genres.some((genre) => genre.toLowerCase() === 'sport') &&
    !asset.genres.some((genre) => nonDateBased.includes(genre.toLowerCase()));

  const isProgram = selectedContext.type === 'tv';
  const isDateBased = (isNews || isSport) && isProgram;

  if (isDateBased) {
    return (
      <Fragment key={selectedContext.id}>
        <h3 className={css.title}>{t('common:episodes-label')}</h3>
        <EpisodeList order="date" episodes={episodes} />
      </Fragment>
    );
  }

  // We use a Fragment with a key to force a re-render when the context changes,
  // so that we can animate the episode list in, even if nothing has changed in the list itself.
  return (
    <Fragment>
      {seasons.length > 0 && (
        <SeasonSelector seasons={seasons} activeSeason={activeSeason} setActiveSeason={setActiveSeason} />
      )}

      <Fragment key={selectedContext.id}>
        <EpisodeList order="chronological" episodes={episodesInSeason} />
      </Fragment>
    </Fragment>
  );
}

export function DetailsSeriesViewSkeleton({ seasonCount }: { seasonCount?: number }) {
  return (
    <Fragment>
      <SeasonSelectorSkeleton count={seasonCount} />
      <EpisodeListSkeleton />
    </Fragment>
  );
}
